import { RefObject, useEffect, useRef } from 'react';

/**
 * 偵測點擊元素外部區域，自動呼叫 callback。
 *
 * @param refs     監聽的 DOM ref，或 ref 陣列（用於 portal / anchor 需排除多個區域的情況）
 * @param callback 點擊外部時的回調
 * @param enabled  是否啟用監聽，預設 true；傳入開關狀態可避免在關閉時掛載多餘 listener
 */
function useClickOutside(
  refs: RefObject<HTMLElement | null> | RefObject<HTMLElement | null>[],
  callback: () => void,
  enabled = true
): void {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (!enabled) return;

    const refsArray = Array.isArray(refs) ? refs : [refs];

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInside = refsArray.some(ref => ref.current?.contains(target));
      if (!isInside) {
        callbackRef.current();
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
    };
  // refs 是穩定的 RefObject，spread 進 deps 是安全的
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...(Array.isArray(refs) ? refs : [refs])]);
}

export default useClickOutside;
